export type TodoistTopicLike = {
  id: string
  source_task_id?: string | null
  todoist_inbox_order?: number | null
  todoist_parent_id?: string | null
  todoist_ancestor_ids?: string[] | null
  todoist_section?: string | null
}

export type TodoistRowHierarchy = { depth: number; hasChildren: boolean }

function todoistOrder(topic: TodoistTopicLike): number {
  return typeof topic.todoist_inbox_order === 'number'
    ? topic.todoist_inbox_order
    : Number.MAX_SAFE_INTEGER
}

export function todoistTaskId(topic: TodoistTopicLike): string | null {
  const taskId = topic.source_task_id?.trim()
  return taskId && !taskId.startsWith('tg_') ? taskId : null
}

// Todoist ancestors without an open topic here (e.g. blocked as not for
// AgentChat) are skipped, so a row nests under its closest shown ancestor
// instead of under whichever unrelated row happens to precede it.
function nearestPresentAncestor(topic: TodoistTopicLike, presentTaskIds: ReadonlySet<string>): string | null {
  const ancestors = topic.todoist_ancestor_ids ?? (topic.todoist_parent_id ? [topic.todoist_parent_id] : [])
  return ancestors.find((taskId) => presentTaskIds.has(taskId)) ?? null
}

function presentTodoistTaskIds(topics: TodoistTopicLike[]): Set<string> {
  return new Set(topics.map(todoistTaskId).filter((taskId): taskId is string => !!taskId))
}

/** Indent and expander state for rows shown together, keyed by topic id. */
export function todoistRowHierarchy<T extends TodoistTopicLike>(topics: T[]): Map<string, TodoistRowHierarchy> {
  const presentTaskIds = presentTodoistTaskIds(topics)
  const depthByTaskId = new Map<string, number>()
  const parentTaskIds = new Set<string>()
  const ordered = [...topics].sort((a, b) => compareTodoistInboxOrder(a, b, () => 0))
  const depthByTopicId = new Map<string, number>()
  for (const topic of ordered) {
    const ancestor = nearestPresentAncestor(topic, presentTaskIds)
    const depth = ancestor ? (depthByTaskId.get(ancestor) ?? 0) + 1 : 0
    if (ancestor) parentTaskIds.add(ancestor)
    const taskId = todoistTaskId(topic)
    if (taskId) depthByTaskId.set(taskId, depth)
    depthByTopicId.set(topic.id, depth)
  }
  return new Map(topics.map((topic) => {
    const taskId = todoistTaskId(topic)
    return [topic.id, {
      depth: depthByTopicId.get(topic.id) ?? 0,
      hasChildren: !!taskId && parentTaskIds.has(taskId),
    }]
  }))
}

export function compareTodoistInboxOrder<T extends TodoistTopicLike>(
  a: T,
  b: T,
  fallbackCompare: (a: T, b: T) => number,
): number {
  const orderDelta = todoistOrder(a) - todoistOrder(b)
  return orderDelta === 0 ? fallbackCompare(a, b) : orderDelta
}

export function groupTodoistTopicsBySection<T extends TodoistTopicLike>(
  topics: T[],
  collapsedTaskIds: ReadonlySet<string>,
): Array<{ name: string; topics: T[] }> {
  const sections = new Map<string, T[]>()
  const ordered = [...topics].sort((a, b) => compareTodoistInboxOrder(a, b, () => 0))
  for (const topic of ordered) {
    // A missing mirror row is unknown, not evidence of an unsectioned task.
    const name = topic.todoist_section || 'Sync pending'
    const section = sections.get(name) ?? []
    section.push(topic)
    sections.set(name, section)
  }
  return [...sections].map(([name, rows]) => ({
    name,
    topics: visibleTodoistTopicsAfterCollapse(rows, collapsedTaskIds),
  }))
}

export function visibleTodoistTopicsAfterCollapse<T extends TodoistTopicLike>(
  topics: T[],
  collapsedTaskIds: ReadonlySet<string>,
): T[] {
  if (collapsedTaskIds.size === 0) return topics

  const presentTaskIds = presentTodoistTaskIds(topics)
  const hiddenTaskIds = new Set<string>()
  const visible: T[] = []

  for (const topic of topics) {
    const taskId = todoistTaskId(topic)
    const parentId = nearestPresentAncestor(topic, presentTaskIds)
    const hiddenByCollapsedParent = !!parentId && (
      collapsedTaskIds.has(parentId) || hiddenTaskIds.has(parentId)
    )

    if (hiddenByCollapsedParent) {
      if (taskId) hiddenTaskIds.add(taskId)
      continue
    }
    visible.push(topic)
  }

  return visible
}
